import React from 'react';
import { 
  User, 
  Shield, 
  Image as ImageIcon,
  File,
  Download,
} from 'lucide-react';

const MessagesList = ({ messages, userId }) => {
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages?.forEach(message => {
      const date = formatDate(message.created_at);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    return groups;
  };

  const renderAttachments = (attachments) => {
    if (!attachments || attachments.length === 0) return null;

    return (
      <div className="mt-3 space-y-2">
        {attachments.map((attachment, index) => {
          const isImage = attachment.file_type === 'image' || 
                         attachment.file_url?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
          
          return (
            <div
              key={index}
              className="flex items-center justify-between p-2 border rounded-lg bg-gray-50"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white rounded">
                  {isImage ? (
                    <ImageIcon className="w-5 h-5 text-gray-500" />
                  ) : (
                    <File className="w-5 h-5 text-gray-500" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {attachment.file_name || `Attachment ${index + 1}`}
                  </p>
                  <p className="text-xs text-gray-500">
                    {attachment.file_size ? formatFileSize(attachment.file_size) : ''}
                  </p>
                </div>
              </div>
              
              <a
                href={attachment.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                title="Download"
              >
                <Download className="w-4 h-4 text-gray-600" />
              </a>
            </div>
          );
        })}
      </div>
    );
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const messageGroups = groupMessagesByDate(messages || []);

  if (!messages || messages.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <User className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No messages yet</h3>
        <p className="text-gray-600">Be the first to send a message in this conversation.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {Object.entries(messageGroups).map(([date, dateMessages]) => (
        <div key={date}>
          {/* Date Separator */}
          <div className="relative flex items-center justify-center my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative px-4">
              <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
                {date}
              </span>
            </div>
          </div>

          {/* Messages for this date */}
          <div className="space-y-4">
            {dateMessages.map((message) => {
              const isCurrentUser = message.sender_id === userId && message.sender_type === 'user';
              const isSystem = message.sender_id === 0 || message.message_type === 'system';
              const isStaff = message.is_staff || message.sender_role === 'support';

              return (
                <div
                  key={message.message_id}
                  className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] md:max-w-[75%] rounded-2xl px-4 py-3 ${
                      isSystem
                        ? 'bg-gray-100'
                        : isCurrentUser
                        ? 'bg-orange-500 text-white'
                        : isStaff
                        ? 'bg-blue-50 border border-blue-100'
                        : 'bg-gray-100'
                    }`}
                  >
                    {/* Message Header */}
                    <div className={`flex items-center justify-between mb-1 ${
                      isCurrentUser ? 'text-orange-100' : 'text-gray-500'
                    }`}>
                      <div className="flex items-center space-x-2">
                        {isSystem ? (
                          <Shield className="w-3 h-3" />
                        ) : isStaff ? (
                          <Shield className="w-3 h-3 text-blue-500" />
                        ) : (
                          <User className="w-3 h-3" />
                        )}
                        <span className="text-xs font-medium">
                          {isSystem ? 'System' : 
                           isStaff ? 'Support Team' :
                           isCurrentUser ? 'You' : 
                           message.sender_name || 'User'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-xs"> @ {formatTime(message.created_at)}</span>
                      </div>
                    </div>

                    {/* Message Content */}
                    <div className={`${
                      isCurrentUser ? 'text-white' : 
                      isSystem ? 'text-gray-700' : 
                      'text-gray-900'
                    }`}>
                      <p className="whitespace-pre-wrap break-words">{message.message}</p>
                      
                      {/* Attachments */}
                      {message.attachments && renderAttachments(message.attachments)}
                      
                      {/* If message only contains attachments placeholder */}
                      {!message.message && message.attachments && message.attachments.length > 0 && (
                        <p className="text-sm italic mt-1">
                          {message.attachments.length} file{message.attachments.length > 1 ? 's' : ''} attached
                        </p>
                      )}
                    </div>

                    {/* Seen indicator */}
                    {/* WhatsApp-style indicators */}
                    {isCurrentUser && (
                      <div className="text-right mt-1">
                        {message.is_read === true ? (
                          <span className="text-xs opacity-70">✓✓ Seen</span>
                        ) : message.is_read === false ? (
                          <span className="text-xs opacity-70">✓✓ Delivered</span>
                        ) : (
                          <span className="text-xs opacity-70">✓ Sent</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default MessagesList;